import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { useTheme } from '../context/ThemeContext';
import '../components/training-library.css';
import { useAuth } from '../context/AuthContext';
import {
  GraduationCap,
  Play,
  X,
  Rocket,
  Webhook,
  Clock,
  Check,
  CheckCircle2,
  Sparkles
} from 'lucide-react';

// Training video catalog — Wistia media IDs resolved from share links.
// thumb/minutes are baked-in defaults; live values are refreshed from the
// Wistia oEmbed API on page load, so changing a thumbnail in Wistia
// automatically updates here.
const TRAINING_SECTIONS = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: Rocket,
    description: 'Everything you need to launch your first AI agent',
    videos: [
      {
        id: 'pokrmtk7ux',
        step: 1,
        title: 'Connecting Your LeadConnector Sub-Account',
        description: 'Link a LeadConnector sub-account to Voxinity AI for calendars, contacts, and tags.',
        tag: 'Setup',
        thumb: 'https://embed-ssl.wistia.com/deliveries/0cb716dc12962dc2c441579bac4ca8ae.jpg',
        minutes: 2.5
      },
      {
        id: '79ydw0l2g2',
        step: 2,
        title: 'Twilio Setup & Phone Number Import',
        description: 'Connect Twilio and import your phone numbers so agents can make and receive calls.',
        tag: 'Setup',
        thumb: 'https://embed-ssl.wistia.com/deliveries/becc37a2ecd48a6694d7db3ebf8450a42cb2f3b1.jpg',
        minutes: 4.3
      },
      {
        id: '4rd915ov2e',
        step: 3,
        title: 'Building Your First AI Agent',
        description: 'Configure voice, prompts, and tools to create a fully working AI agent.',
        tag: 'Agents',
        thumb: 'https://embed-ssl.wistia.com/deliveries/1f740876ce55993605da9d6fdd266a70.jpg',
        minutes: 29.1
      },
      {
        id: 'tdo7x4symn',
        step: 4,
        title: 'Embedding the Voice Orb on Your Website',
        description: 'Set up the voice orb widget and embed it on any website in minutes.',
        tag: 'Widgets',
        thumb: 'https://embed-ssl.wistia.com/deliveries/1c010a567d31ade963d295f540cc6012.jpg',
        minutes: 12.7
      },
      {
        id: 'ywxvo4snw1nv935',
        step: 5,
        title: 'AI Chat Widget Setup',
        description: 'Configure the AI chat widget, customize its look, and embed it on any site.',
        tag: 'Widgets',
        thumb: 'https://embed-ssl.wistia.com/deliveries/1c010a567d31ade963d295f540cc6012.jpg',
        minutes: 8.0
      },
      {
        id: '9evd8soa4g',
        step: 6,
        title: 'Client Onboarding Engine — Complete Setup Walkthrough',
        description: 'Step-by-step: build your onboarding form, embed it, wire the GHL webhook, and let the AI Wizard auto-create client agents.',
        tag: 'Onboarding',
        thumb: 'https://embed-ssl.wistia.com/deliveries/31b3896faf1e68c826ff8a5d6a0abd9f.jpg',
        minutes: 69.4
      }
    ]
  },
  {
    id: 'automation-webhooks',
    label: 'Automation Webhooks',
    icon: Webhook,
    description: 'Trigger calls and AI actions from your workflows',
    videos: [
      {
        id: 'ptnb2rn4uw',
        step: 7,
        title: 'Outbound Call Webhook',
        description: 'Trigger AI outbound calls automatically from your workflows.',
        tag: 'Webhooks',
        thumb: 'https://embed-ssl.wistia.com/deliveries/9eda4365c12639a68f0af66c925cfb55.jpg',
        minutes: 12.2
      },
      {
        id: 'ux81d8ehey',
        step: 8,
        title: 'Callback Automation',
        description: 'Let inbound leads request a callback and have your agent dial them back.',
        tag: 'Webhooks',
        thumb: 'https://embed-ssl.wistia.com/deliveries/e2fc6c1cb7e81ac91876fef1f9f9392089ae7a2c.jpg',
        minutes: 6.5
      },
      {
        id: 'yrqhbua12m',
        step: 9,
        title: 'Follow-Up Webhook',
        description: 'Automate intelligent follow-up touchpoints after calls and conversations.',
        tag: 'Webhooks',
        thumb: 'https://embed-ssl.wistia.com/deliveries/a74a76755608396001e5a65a0012d1c0.jpg',
        minutes: 3.6
      },
      {
        id: '27q3emnmrd',
        step: 10,
        title: 'Website Scraper Webhook',
        description: 'Pull live website content into your agent\u2019s knowledge with the scraper webhook.',
        tag: 'Webhooks',
        thumb: 'https://embed-ssl.wistia.com/deliveries/bcd3958c7fa82d8f56295a417ec2d0ce4da3724c.jpg',
        minutes: 6.0
      },
      {
        id: 'uu140w7iqo',
        step: 11,
        title: 'Screenshot Webhook',
        description: 'Capture website screenshots on demand and use them in your automations.',
        tag: 'Webhooks',
        thumb: 'https://embed-ssl.wistia.com/deliveries/40a60783d8321ab1a9e001e1308dbc457cdfa07a.jpg',
        minutes: 3.0
      }
    ]
  }
];

const ALL_VIDEOS = TRAINING_SECTIONS.flatMap((s) => s.videos);

const formatDuration = (minutes) => {
  if (minutes < 10) {
    const rounded = Math.round(minutes * 2) / 2; // nearest 0.5
    return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1)} min`;
  }
  return `${Math.round(minutes)} min`;
};

const thumbUrl = (thumb, size = '640x360') => `${thumb}?image_crop_resized=${size}`;

const DurationChip = ({ minutes }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-black/60 text-white backdrop-blur-sm border border-white/10">
    <Clock size={10} />
    {formatDuration(minutes)}
  </span>
);

const VideoCard = ({ video, meta, isDarkMode, onPlay, onToggleWatched, isWatched, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      data-testid={`training-video-card-${video.id}`}
      className="training-video-card group relative cursor-pointer"
      role="button" tabIndex={0} aria-label={`Watch ${video.title}`}
      onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onPlay(video); } }}
      onClick={() => onPlay(video)}
    >
      {/* Ambient glow halo — always visible (subtle), intensifies on hover.
          Sits behind the card via a sibling absolute layer with negative
          inset so the glow extends past the card edges. */}
      <div
        aria-hidden
        className={`absolute -inset-1 rounded-3xl blur-xl transition-opacity duration-300 pointer-events-none ${
          isDarkMode
            ? 'bg-gradient-to-br from-teal-400/15 via-cyan-400/10 to-teal-400/15 opacity-30 group-hover:opacity-100'
            : 'bg-gradient-to-br from-teal-300/30 via-cyan-300/20 to-teal-300/30 opacity-40 group-hover:opacity-90'
        }`}
      />

      {/* Card body */}
      <div
        className={`relative rounded-2xl border overflow-hidden transition-all duration-200 ${
          isDarkMode
            ? `bg-gradient-to-br from-space-800/90 to-space-900/95 ${
                isWatched ? 'border-teal-400/40 ring-1 ring-teal-400/20' : 'border-white/10 ring-1 ring-white/[0.04]'
              } group-hover:border-teal-400/60 group-hover:ring-teal-400/30 group-hover:shadow-[0_20px_60px_-12px_rgba(45,212,191,0.4)]`
            : `bg-white ${
                isWatched ? 'border-teal-500/40 ring-1 ring-teal-500/15' : 'border-gray-200 ring-1 ring-gray-100'
              } shadow-[0_8px_24px_-12px_rgba(15,23,42,0.12)] group-hover:border-teal-500/50 group-hover:ring-teal-400/25 group-hover:shadow-[0_20px_50px_-12px_rgba(20,184,166,0.35)]`
        }`}
      >
        {/* Thumbnail */}
        <div className="relative h-44 overflow-hidden bg-black">
          <img
            src={thumbUrl(meta.thumb)}
            alt={video.title}
            loading="lazy"
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06] ${
              isWatched ? 'opacity-85' : ''
            }`}
          />
          {/* Bottom gradient for chip legibility */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-black/55 backdrop-blur-md border border-white/25 shadow-lg transition-all duration-200 group-hover:scale-110 group-hover:bg-teal-400 group-hover:border-teal-200 group-hover:shadow-[0_0_36px_rgba(45,212,191,0.7)]">
              <Play size={22} className="text-white ml-0.5" fill="currentColor" />
            </div>
          </div>
          {/* Step badge */}
          <span className="absolute top-3 left-3 text-[11px] font-semibold px-2 py-0.5 rounded-full tracking-wide bg-black/70 text-white backdrop-blur-sm border border-white/15">
            {String(video.step).padStart(2, '0')}
          </span>
          {/* Tag chip */}
          <span className="absolute top-3 right-3 text-[11px] font-medium px-2 py-0.5 rounded-full bg-teal-400/25 text-teal-100 backdrop-blur-md border border-teal-300/40 shadow-sm">
            {video.tag}
          </span>
          {/* Duration */}
          <span className="absolute bottom-3 left-3">
            <DurationChip minutes={meta.minutes} />
          </span>
          {/* Watched toggle */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleWatched(video); }}
            data-testid={`training-watched-toggle-${video.id}`}
            title={isWatched ? 'Mark as unwatched' : 'Mark as watched'}
            className={`absolute bottom-3 right-3 z-20 flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border backdrop-blur-sm transition-all duration-200 ${
              isWatched
                ? 'bg-teal-400/85 text-white border-teal-300/70'
                : 'bg-black/70 text-gray-200 border-white/15 opacity-0 group-hover:opacity-100 hover:text-teal-300 hover:border-teal-400/50'
            }`}
          >
            <Check size={12} strokeWidth={3} />
            {isWatched ? 'Watched' : 'Mark watched'}
          </button>
        </div>

        {/* Thin teal accent line — visually anchors the video frame and
            separates the thumbnail from the text block. */}
        <div className={`h-px ${
          isDarkMode
            ? 'bg-gradient-to-r from-transparent via-teal-400/50 to-transparent'
            : 'bg-gradient-to-r from-transparent via-teal-400/40 to-transparent'
        }`} />

        {/* Text */}
        <div className="p-5">
          <h3 className={`flex items-center gap-2 font-semibold text-[15px] leading-snug mb-1.5 transition-colors ${
            isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'
          }`}>
            {video.title}
            {isWatched && (
              <CheckCircle2 size={15} className="text-teal-400 flex-shrink-0" data-testid={`training-watched-check-${video.id}`} />
            )}
          </h3>
          <p className={`text-[13px] leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {video.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default function TrainingPage() {
  const { isDarkMode } = useTheme();
  const { api } = useAuth();
  const [activeVideo, setActiveVideo] = useState(null);
  const [watched, setWatched] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [meta, setMeta] = useState(() =>
    Object.fromEntries(ALL_VIDEOS.map((v) => [v.id, { thumb: v.thumb, minutes: v.minutes }]))
  );

  // Close modal on Escape
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setActiveVideo(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Load saved progress
  useEffect(() => {
    api.get('/api/auth/training-progress')
      .then((r) => setWatched(r.data?.watched || []))
      .catch(() => {});
  }, [api]);

  // Refresh thumbnails + durations live from Wistia oEmbed (CORS-enabled),
  // so thumbnail changes made in Wistia show up without a code change.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled(
      ALL_VIDEOS.map((v) =>
        fetch(`https://fast.wistia.net/oembed?url=https://home.wistia.com/medias/${v.id}&format=json`)
          .then((r) => r.json())
          .then((d) => ({ id: v.id, thumb: (d.thumbnail_url || '').split('?')[0], minutes: (d.duration || 0) / 60 }))
      )
    ).then((results) => {
      if (cancelled) return;
      setMeta((prev) => {
        const next = { ...prev };
        results.forEach((res) => {
          if (res.status === 'fulfilled' && res.value.thumb) {
            next[res.value.id] = {
              thumb: res.value.thumb,
              minutes: res.value.minutes || prev[res.value.id].minutes
            };
          }
        });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, []);

  const persistWatched = useCallback((videoId, isWatched) => {
    setWatched((prev) => {
      if (isWatched) return prev.includes(videoId) ? prev : [...prev, videoId];
      return prev.filter((id) => id !== videoId);
    });
    api.post('/api/auth/training-progress', { video_id: videoId, watched: isWatched }).catch(() => {});
  }, [api]);

  const handlePlay = useCallback((video) => {
    setActiveVideo(video);
    if (!watched.includes(video.id)) persistWatched(video.id, true);
  }, [watched, persistWatched]);

  const handleToggleWatched = useCallback((video) => {
    persistWatched(video.id, !watched.includes(video.id));
  }, [watched, persistWatched]);

  const totalVideos = ALL_VIDEOS.length;
  const watchedCount = ALL_VIDEOS.filter((v) => watched.includes(v.id)).length;
  const progressPct = Math.round((watchedCount / totalVideos) * 100);
  const totalMinutes = Math.round(ALL_VIDEOS.reduce((n, v) => n + (meta[v.id]?.minutes || v.minutes), 0));
  const upNext = ALL_VIDEOS.find((v) => !watched.includes(v.id));

  return (
    <DashboardLayout>
      <div className="training-library p-6 md:p-8 mx-auto" data-theme={isDarkMode ? 'dark' : 'light'} data-testid="training-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          className="training-library-header"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isDarkMode ? 'bg-teal-400/10' : 'bg-teal-500/10'
            }`}>
              <GraduationCap size={22} className="text-teal-400" />
            </div>
            <h1 className={`text-2xl md:text-3xl font-display font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Training Center
            </h1>
          </div>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center gap-2 mb-5`}>
            Master Voxinity AI step by step
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
              isDarkMode ? 'bg-white/5 text-gray-400 border border-white/10' : 'bg-gray-100 text-gray-500 border border-gray-200'
            }`}>
              <Clock size={11} /> {totalVideos} video tutorials · ~{totalMinutes} min
            </span>
          </p>

          {/* Progress bar */}
          <div
            className={`max-w-md rounded-xl border p-4 ${
              isDarkMode ? 'bg-space-800/60 border-white/5' : 'bg-white border-gray-200'
            }`}
            data-testid="training-progress-card"
          >
            <div className="flex items-center justify-between mb-2.5">
              <span className={`text-[13px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {watchedCount === totalVideos ? (
                  <span className="inline-flex items-center gap-1.5 text-teal-400">
                    <CheckCircle2 size={15} /> All tutorials watched
                  </span>
                ) : (
                  <>Your progress</>
                )}
              </span>
              <span className="text-[13px] font-semibold text-teal-400" data-testid="training-progress-count">
                {watchedCount} of {totalVideos} watched
              </span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="h-full rounded-full bg-gradient-to-r from-teal-400 to-cyan-400"
              />
            </div>
          </div>
        </motion.div>

        {/* Up Next hero */}
        <div className="training-library-controls">
          <div className="training-categories" data-testid="training-categories">
            {[{id:'all',label:'All videos'}, ...TRAINING_SECTIONS].map(section => <button type="button" key={section.id}
              aria-pressed={category === section.id} data-testid={`training-category-${section.id}`} onClick={() => setCategory(section.id)}>{section.label}</button>)}
          </div>
          <label className="training-search"><span aria-hidden="true">⌕</span><input aria-label="Search training videos" data-testid="training-search" placeholder="Search videos" value={search} onChange={event => setSearch(event.target.value)} /></label>
        </div>
        {upNext && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => handlePlay(upNext)}
            role="button" tabIndex={0} aria-label={`Watch next: ${upNext.title}`}
            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handlePlay(upNext); } }}
            data-testid="training-up-next-hero"
            className="group relative mb-12 cursor-pointer"
          >
            {/* Ambient halo — bolder than the regular cards since this is hero */}
            <div
              aria-hidden
              className={`absolute -inset-1.5 rounded-3xl blur-2xl transition-opacity duration-300 pointer-events-none ${
                isDarkMode
                  ? 'bg-gradient-to-r from-teal-400/20 via-cyan-400/15 to-teal-400/20 opacity-50 group-hover:opacity-100'
                  : 'bg-gradient-to-r from-teal-300/40 via-cyan-300/30 to-teal-300/40 opacity-60 group-hover:opacity-100'
              }`}
            />
            <div className={`relative rounded-2xl border overflow-hidden transition-all duration-200 ${
              isDarkMode
                ? 'bg-gradient-to-br from-space-800/90 to-space-900/95 border-white/10 ring-1 ring-white/[0.04] group-hover:border-teal-400/60 group-hover:ring-teal-400/30 group-hover:shadow-[0_28px_80px_-12px_rgba(45,212,191,0.5)]'
                : 'bg-white border-gray-200 ring-1 ring-gray-100 shadow-[0_8px_28px_-12px_rgba(15,23,42,0.15)] group-hover:border-teal-500/50 group-hover:ring-teal-400/30 group-hover:shadow-[0_28px_60px_-12px_rgba(20,184,166,0.4)]'
            }`}>
            <div className="flex flex-col md:flex-row">
              {/* Hero thumbnail */}
              <div className="relative md:w-[46%] h-56 md:h-auto overflow-hidden bg-black flex-shrink-0">
                <img
                  src={thumbUrl(meta[upNext.id].thumb, '960x540')}
                  alt={upNext.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/30 hidden md:block" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-black/55 backdrop-blur-md border border-white/25 shadow-lg transition-all duration-200 group-hover:scale-110 group-hover:bg-teal-400 group-hover:border-teal-200 group-hover:shadow-[0_0_36px_rgba(45,212,191,0.7)]">
                    <Play size={26} className="text-white ml-1" fill="currentColor" />
                  </div>
                </div>
                <span className="absolute bottom-3 left-3">
                  <DurationChip minutes={meta[upNext.id].minutes} />
                </span>
              </div>

              {/* Hero content */}
              <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-teal-400" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-400">
                    {watchedCount === 0 ? 'Start Here' : 'Up Next'}
                  </span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                    isDarkMode ? 'bg-white/5 text-gray-400 border border-white/10' : 'bg-gray-100 text-gray-500 border border-gray-200'
                  }`}>
                    Step {String(upNext.step).padStart(2, '0')} of {totalVideos}
                  </span>
                </div>
                <h2 className={`text-xl md:text-2xl font-display font-semibold mb-2 transition-colors ${
                  isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'
                }`}>
                  {upNext.title}
                </h2>
                <p className={`text-sm leading-relaxed mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {upNext.description}
                </p>
                <div>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-400 to-cyan-400 text-space-900 transition-transform duration-200 group-hover:scale-[1.03]">
                    <Play size={15} fill="currentColor" />
                    {watchedCount === 0 ? 'Start watching' : 'Continue'}
                  </span>
                </div>
              </div>
            </div>
            </div>
          </motion.div>
        )}

        {/* Sections */}
        {TRAINING_SECTIONS.map((section, sIdx) => {
          if (category !== 'all' && section.id !== category) return null;
          const videos = section.videos.filter(video => `${video.title} ${video.description}`.toLowerCase().includes(search.toLowerCase().trim()));
          if (!videos.length) return null;
          const SectionIcon = section.icon;
          return (
            <div key={section.id} className={sIdx > 0 ? 'mt-12' : ''} data-testid={`training-section-${section.id}`}>
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + sIdx * 0.1 }}
                className="flex items-center gap-2.5 mb-1"
              >
                <SectionIcon size={16} className="text-teal-400" />
                <h2 className={`text-xs font-semibold uppercase tracking-[0.14em] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {section.label}
                </h2>
                <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/5' : 'bg-gray-200'}`} />
              </motion.div>
              <p className={`text-[13px] mb-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {section.description}
              </p>

              <div className="training-grid grid">
                {videos.map((video, vIdx) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    meta={meta[video.id]}
                    isDarkMode={isDarkMode}
                    onPlay={handlePlay}
                    onToggleWatched={handleToggleWatched}
                    isWatched={watched.includes(video.id)}
                    delay={0.15 + sIdx * 0.1 + vIdx * 0.05}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {!TRAINING_SECTIONS.some(section => (category === 'all' || category === section.id) && section.videos.some(video => `${video.title} ${video.description}`.toLowerCase().includes(search.toLowerCase().trim()))) &&
          <p className="text-sm py-12 text-center text-gray-500" data-testid="training-search-empty">No videos match your search.</p>}
        {/* Video Modal */}
        <AnimatePresence>
          {activeVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm"
              onClick={() => setActiveVideo(null)}
              data-testid="training-video-modal"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 12 }}
                transition={{ duration: 0.2 }}
                className={`training-watch-shell relative w-full max-w-4xl rounded-2xl overflow-hidden border ${
                  isDarkMode ? 'bg-space-900 border-white/10' : 'bg-white border-gray-200'
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal header */}
                <div className={`flex items-center justify-between gap-4 px-5 py-4 border-b ${
                  isDarkMode ? 'border-white/5' : 'border-gray-100'
                }`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-400/10 text-teal-400 border border-teal-400/20 flex-shrink-0">
                      {String(activeVideo.step).padStart(2, '0')}
                    </span>
                    <h3 className={`font-semibold text-sm md:text-base truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {activeVideo.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => setActiveVideo(null)}
                    data-testid="training-video-modal-close"
                    aria-label="Back to training"
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                      isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <X size={18} />
                  </button>
                </div>
                {/* Player */}
                <div className="training-watch-layout">
                <div>
                <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                  <iframe
                    src={`https://fast.wistia.net/embed/iframe/${activeVideo.id}?seo=false&videoFoam=true&autoPlay=true`}
                    title={activeVideo.title}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                  />
                </div>
                <p className="training-watch-description" data-testid="training-watch-description">{activeVideo.description}</p>
                </div>
                <aside className="training-watch-queue" data-testid="training-watch-queue">
                  <h4>Lessons</h4>
                  {ALL_VIDEOS.map(video => <button type="button" key={video.id} className="training-queue-item"
                    data-testid={`training-queue-${video.id}`} aria-current={activeVideo.id === video.id} onClick={() => handlePlay(video)}>
                    <img src={thumbUrl(meta[video.id].thumb)} alt="" loading="lazy" /><span>{video.title}</span>
                  </button>)}
                </aside>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
